import { Controller } from "@hotwired/stimulus"

const BLOCK_TYPES = [
  { type: "heading", label: "Heading", icon: "fa-heading", category: "content" },
  { type: "text", label: "Text", icon: "fa-paragraph", category: "content" },
  { type: "divider", label: "Divider", icon: "fa-minus", category: "content" },
  { type: "merge_field", label: "Merge Field", icon: "fa-code", category: "content" },
  { type: "short_answer", label: "Short Answer", icon: "fa-font", category: "field" },
  { type: "long_answer", label: "Long Answer", icon: "fa-align-left", category: "field" },
  { type: "date", label: "Date", icon: "fa-calendar", category: "field" },
  { type: "single_select", label: "Single Select", icon: "fa-circle-dot", category: "field" },
  { type: "multi_select", label: "Multi Select", icon: "fa-square-check", category: "field" },
  { type: "checkbox", label: "Checkbox", icon: "fa-check-square", category: "field" },
  { type: "phone", label: "Phone", icon: "fa-phone", category: "field" },
  { type: "email", label: "Email", icon: "fa-envelope", category: "field" }
]

// Fallback — overridden by data-document-builder-merge-tags-value from server
const DEFAULT_MERGE_TAGS = [
  "client_name", "client_first_name", "client_last_name",
  "client_email", "practice_name", "practitioner_name", "date"
]

export default class extends Controller {
  static targets = ["blockList", "hiddenInput", "menu", "previewModal", "previewContent"]
  static values = { blocks: { type: Array, default: [] }, activeBlockId: { type: String, default: "" }, mergeTags: { type: Object, default: {} } }

  get mergeTagGroups() {
    const groups = this.mergeTagsValue
    if (groups && Object.keys(groups).length > 0) return groups
    // Fallback if data attribute not provided
    return {
      "Client": DEFAULT_MERGE_TAGS.filter(t => t.startsWith("client_")),
      "Practice": DEFAULT_MERGE_TAGS.filter(t => t.startsWith("practice_")),
      "Practitioner": DEFAULT_MERGE_TAGS.filter(t => t.startsWith("practitioner_")),
      "Document": DEFAULT_MERGE_TAGS.filter(t => !t.includes("_"))
    }
  }

  get allMergeTags() {
    return Object.values(this.mergeTagGroups).flat()
  }

  connect() {
    this.render()
    document.addEventListener("click", this.closeMenu)
    this._form = this.element.closest("form")
    if (this._form) {
      this._form.addEventListener("submit", this._handleFormSubmit)
    }
  }

  disconnect() {
    document.removeEventListener("click", this.closeMenu)
    if (this._form) {
      this._form.removeEventListener("submit", this._handleFormSubmit)
      this._form = null
    }
  }

  _handleFormSubmit = (event) => {
    this.validateBeforeSave(event)
  }

  closeMenu = (event) => {
    if (this.hasMenuTarget && !event.target.closest("[data-document-builder-target='menu']") && !event.target.closest("[data-add-block-btn]")) {
      this.hideMenu()
    }
  }

  // --- Block CRUD ---

  addBlock(event) {
    const type = event.currentTarget.dataset.blockType
    // Menu buttons have empty afterIndex — read from the menu container instead
    let rawIndex = event.currentTarget.dataset.afterIndex
    if (rawIndex === "" || rawIndex === undefined) {
      rawIndex = this.hasMenuTarget ? this.menuTarget.dataset.afterIndex : "-1"
    }
    const afterIndex = parseInt(rawIndex ?? "-1")

    const block = {
      id: crypto.randomUUID(),
      type: type,
      content: "",
      position: 0,
      settings: this.defaultSettings(type)
    }

    const blocks = [...this.blocksValue]
    const insertAt = afterIndex + 1
    blocks.splice(insertAt, 0, block)
    this.reindex(blocks)
    this.blocksValue = blocks
    this.activeBlockIdValue = block.id
    this.render()
    this.hideMenu()

    // Focus new block after render
    requestAnimationFrame(() => {
      const el = this.blockListTarget.querySelector(`[data-block-id="${block.id}"] [contenteditable], [data-block-id="${block.id}"] input`)
      if (el) el.focus()
    })
  }

  removeBlock(event) {
    const id = event.currentTarget.closest("[data-block-id]").dataset.blockId
    const blocks = this.blocksValue.filter(b => b.id !== id)
    this.reindex(blocks)
    this.blocksValue = blocks
    this.render()
  }

  moveBlock(event) {
    const id = event.currentTarget.closest("[data-block-id]").dataset.blockId
    const direction = parseInt(event.currentTarget.dataset.direction)
    const blocks = [...this.blocksValue]
    const idx = blocks.findIndex(b => b.id === id)
    const newIdx = idx + direction
    if (newIdx < 0 || newIdx >= blocks.length) return
    ;[blocks[idx], blocks[newIdx]] = [blocks[newIdx], blocks[idx]]
    this.reindex(blocks)
    this.blocksValue = blocks
    this.render()
  }

  updateBlockContent(event) {
    const id = event.currentTarget.closest("[data-block-id]").dataset.blockId
    const blocks = [...this.blocksValue]
    const block = blocks.find(b => b.id === id)
    if (block) {
      block.content = event.currentTarget.innerHTML
      this.blocksValue = blocks
      this.syncHiddenInput()
    }
  }

  updateBlockSetting(event) {
    const id = event.currentTarget.closest("[data-block-id]").dataset.blockId
    const key = event.currentTarget.dataset.settingKey
    const blocks = [...this.blocksValue]
    const block = blocks.find(b => b.id === id)
    if (block) {
      if (!block.settings) block.settings = {}
      const val = event.currentTarget.type === "checkbox"
        ? event.currentTarget.checked
        : event.currentTarget.value
      block.settings[key] = val
      this.blocksValue = blocks
      this.syncHiddenInput()
    }
  }

  updateBlockPlainContent(event) {
    const id = event.currentTarget.closest("[data-block-id]").dataset.blockId
    const blocks = [...this.blocksValue]
    const block = blocks.find(b => b.id === id)
    if (block) {
      block.content = event.currentTarget.value
      this.blocksValue = blocks
      this.syncHiddenInput()
    }
  }

  // --- Menu ---

  showMenu(event) {
    event.stopPropagation()
    const afterIndex = event.currentTarget.dataset.afterIndex ?? "-1"
    const rect = event.currentTarget.getBoundingClientRect()

    if (!this.hasMenuTarget) return
    this.menuTarget.dataset.afterIndex = afterIndex

    // Use fixed positioning to float above all content
    this.menuTarget.style.position = "fixed"
    this.menuTarget.style.top = `${rect.bottom + 4}px`

    // Center on mobile, align to button on desktop
    if (window.innerWidth < 640) {
      this.menuTarget.style.left = "1rem"
    } else {
      this.menuTarget.style.left = `${rect.left}px`
    }

    // If menu would overflow bottom of viewport, show above the button instead
    const menuHeight = 320 // max-h-80 = 20rem = 320px
    if (rect.bottom + 4 + menuHeight > window.innerHeight) {
      this.menuTarget.style.top = `${rect.top - menuHeight - 4}px`
    }

    this.menuTarget.classList.remove("hidden")
  }

  hideMenu() {
    if (this.hasMenuTarget) {
      this.menuTarget.classList.add("hidden")
    }
  }

  // --- Save Validation ---

  validateBeforeSave(event) {
    this.syncHiddenInput()

    const unconfigured = this.findUnconfiguredBlocks()
    if (unconfigured.length === 0) return // allow submit

    event.preventDefault()

    const count = unconfigured.length
    const types = [...new Set(unconfigured.map(b => {
      const info = BLOCK_TYPES.find(t => t.type === b.type)
      return info ? info.label : b.type
    }))]

    const form = event.target
    const message = `${count} block${count > 1 ? "s" : ""} not configured: ${types.join(", ")}. Clients will see empty space where these blocks appear.`

    // Native confirm() is a UX/brand violation — replaced with the
    // shared confirm-dialog controller via the programmatic event API.
    document.dispatchEvent(new CustomEvent("confirm-dialog:show", {
      detail: {
        title: "Save with empty blocks?",
        message: message,
        confirmText: "Save anyway",
        variant: "primary",
        onConfirm: () => form.submit()
      }
    }))
  }

  findUnconfiguredBlocks() {
    return this.blocksValue.filter(block => {
      switch (block.type) {
        case "heading":
        case "text": {
          const tmp = document.createElement("div")
          tmp.innerHTML = block.content || ""
          return !tmp.textContent.trim()
        }
        case "divider":
        case "merge_field":
        case "signature":
          return false
        case "checkbox":
          return !(block.settings?.label || "").trim()
        default:
          return !(block.settings?.label || "").trim()
      }
    })
  }

  // --- Text Formatting ---

  formatText(event) {
    event.preventDefault()
    const command = event.currentTarget.dataset.command
    // TODO(rte-migration): document.execCommand is deprecated but still
    // supported by every major browser. Replacing it cleanly requires a
    // real RTE library (Lexical / Prosemirror / TipTap) — out of scope
    // for this hardening pass. Track the migration separately.
    document.execCommand(command, false, null)
    this.updateFormatButtons(event.currentTarget.closest("[data-block-id]"))
  }

  updateFormatButtons(blockEl) {
    if (!blockEl) return
    const toolbar = blockEl.querySelector("[data-text-toolbar]")
    if (!toolbar) return

    const commands = ["bold", "italic", "underline", "insertUnorderedList", "insertOrderedList", "indent", "outdent"]
    toolbar.querySelectorAll("[data-command]").forEach(btn => {
      const command = btn.dataset.command
      if (!commands.includes(command)) return
      const isActive = document.queryCommandState(command)
      if (isActive) {
        btn.classList.add("bg-primary-100", "text-primary-700")
        btn.classList.remove("text-neutral-500")
      } else {
        btn.classList.remove("bg-primary-100", "text-primary-700")
        btn.classList.add("text-neutral-500")
      }
    })
  }

  // --- Preview ---

  openPreview() {
    if (!this.hasPreviewModalTarget) return
    this.renderPreview()
    this.previewModalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  closePreview() {
    if (!this.hasPreviewModalTarget) return
    this.previewModalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
  }

  renderPreview() {
    const blocks = [...this.blocksValue].sort((a, b) => (a.position || 0) - (b.position || 0))
    const container = this.previewContentTarget

    const unconfiguredBanner = (type) => `
      <div class="mb-4 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
        <i class="fa-solid fa-triangle-exclamation text-amber-500 text-xs"></i>
        <span class="text-sm text-amber-700">${type} block is not configured</span>
      </div>`

    const html = blocks.map(block => {
      switch (block.type) {
        case "heading": {
          const text = (block.content || "").trim()
          if (!text) return unconfiguredBanner("Heading")
          const level = block.settings?.level || "h2"
          const sizes = { h1: "text-2xl font-bold", h2: "text-lg font-semibold", h3: "text-base font-semibold" }
          return `<div class="mb-4"><${level} class="${sizes[level] || sizes.h2} text-neutral-900">${text}</${level}></div>`
        }
        case "text": {
          const tmp = document.createElement("div")
          tmp.innerHTML = block.content || ""
          if (!tmp.textContent.trim()) return unconfiguredBanner("Text")
          return `<div class="mb-4 text-sm text-neutral-800 leading-relaxed">${block.content}</div>`
        }
        case "divider":
          return `<hr class="my-6 border-neutral-200" />`
        case "merge_field": {
          const field = block.settings?.field || "practice_name"
          const labels = {
            client_name: "Jane Doe", client_first_name: "Jane", client_last_name: "Doe",
            client_email: "jane@example.com", client_phone: "(555) 123-4567", client_date_of_birth: "January 15, 1990",
            practice_name: "Your Practice", practice_phone: "(555) 987-6543", practice_address: "123 Main St, Suite 200",
            practitioner_name: "Dr. Smith", practitioner_credentials: "LPC, NCC",
            date: "April 5, 2026", year: "2026"
          }
          return `<div class="mb-4"><span class="text-sm font-medium text-neutral-900 bg-primary-50 px-1.5 py-0.5 rounded">${labels[field] || field}</span></div>`
        }
        case "short_answer":
        case "long_answer":
        case "date":
        case "phone":
        case "email": {
          const label = (block.settings?.label || "").trim()
          const fieldType = BLOCK_TYPES.find(t => t.type === block.type)?.label || "Field"
          if (!label) return unconfiguredBanner(fieldType)
          const req = block.settings?.required ? " *" : ""
          const help = block.settings?.help_text ? `<p class="mt-1 text-xs text-neutral-500">${this.escapeHtml(block.settings.help_text)}</p>` : ""
          const inputType = block.type === "long_answer"
            ? `<textarea disabled rows="${block.settings?.rows || 4}" class="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm bg-neutral-50"></textarea>`
            : `<input type="text" disabled placeholder="${this.escapeHtml(block.settings?.placeholder || "")}" class="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm bg-neutral-50" />`
          return `<div class="mb-4"><label class="block text-sm font-medium text-neutral-700 mb-1">${this.escapeHtml(label)}${req}</label>${inputType}${help}</div>`
        }
        case "single_select":
        case "multi_select": {
          const label = (block.settings?.label || "").trim()
          const fieldType = BLOCK_TYPES.find(t => t.type === block.type)?.label || "Field"
          if (!label) return unconfiguredBanner(fieldType)
          const req = block.settings?.required ? " *" : ""
          const options = (block.settings?.options || "").split(",").map(o => o.trim()).filter(o => o)
          const type = block.type === "single_select" ? "radio" : "checkbox"
          const optionsHtml = options.length > 0
            ? options.map(o => `<label class="flex items-center gap-2 text-sm text-neutral-700"><input type="${type}" disabled class="text-primary-600" /> ${this.escapeHtml(o)}</label>`).join("")
            : `<p class="text-xs text-neutral-400 italic">No options defined</p>`
          return `<div class="mb-4"><label class="block text-sm font-medium text-neutral-700 mb-1">${this.escapeHtml(label)}${req}</label><div class="space-y-2">${optionsHtml}</div></div>`
        }
        case "checkbox": {
          const label = (block.settings?.label || "").trim()
          if (!label) return unconfiguredBanner("Checkbox")
          return `<div class="mb-4"><label class="flex items-start gap-2 text-sm text-neutral-700"><input type="checkbox" disabled class="mt-0.5 rounded border-neutral-300 text-primary-600" /> <span>${this.escapeHtml(label)}</span></label></div>`
        }
        case "signature": {
          const label = (block.settings?.label || "").trim()
          return `<div class="mb-4"><label class="block text-sm font-medium text-neutral-700 mb-2">${this.escapeHtml(label || "Signature")}</label><div class="border border-neutral-300 rounded-lg p-8 bg-neutral-50 text-center"><i class="fa-solid fa-signature text-2xl text-neutral-300"></i><p class="text-sm text-neutral-400 mt-2">Signature area</p></div></div>`
        }
        default:
          return ""
      }
    }).join("")

    container.innerHTML = html || `<p class="text-sm text-neutral-500 italic">No blocks to preview. Add content blocks to see a preview.</p>`
  }

  // --- Rendering ---

  render() {
    this.blockListTarget.innerHTML = this.blocksValue
      .map((block, index) => this.renderBlock(block, index))
      .join("") + this.renderAddButton(this.blocksValue.length - 1)

    this.element.querySelector("[data-document-builder-target='menu']")?.remove()
    this.blockListTarget.insertAdjacentHTML("afterend", this.renderMenu())

    this.syncHiddenInput()
  }

  renderBlock(block, index) {
    const isExpanded = this.activeBlockIdValue === block.id

    if (isExpanded) {
      const toolbar = this.renderBlockToolbar(block, index)
      const content = this.renderBlockContent(block)
      return `
        <div class="group relative border border-primary-200 bg-white rounded-lg p-3 pb-5 mb-6 shadow-sm" data-block-id="${block.id}">
          ${toolbar}
          ${content}
          ${this.renderInlineAddButton(index, true)}
        </div>
      `
    } else {
      return `
        <div class="group relative bg-white border border-neutral-200 hover:border-primary-300 hover:shadow-sm rounded-lg mb-2 transition-all cursor-pointer" data-block-id="${block.id}" data-action="click->document-builder#expandBlock">
          ${this.renderCollapsedBlock(block, index)}
          ${this.renderInlineAddButton(index, false)}
        </div>
      `
    }
  }

  renderCollapsedBlock(block, index) {
    const typeInfo = BLOCK_TYPES.find(t => t.type === block.type)
    const label = typeInfo ? typeInfo.label : block.type
    const icon = typeInfo?.icon || "fa-cube"
    const summary = this.blockSummary(block)

    return `
      <div class="flex items-center gap-2 sm:gap-3 px-3 py-2">
        <i class="fa-solid ${icon} text-xs text-neutral-400 w-4 text-center hidden sm:block"></i>
        <span class="text-xs font-medium text-neutral-400 uppercase tracking-wider shrink-0">${label}</span>
        <span class="text-sm text-neutral-600 truncate flex-1 hidden sm:block">${summary}</span>
        <div class="flex items-center gap-1 shrink-0 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          <button type="button" class="p-1 text-neutral-400 hover:text-neutral-600 cursor-pointer" data-action="click->document-builder#moveBlock" data-direction="-1" title="Move up">
            <i class="fa-solid fa-chevron-up text-xs"></i>
          </button>
          <button type="button" class="p-1 text-neutral-400 hover:text-neutral-600 cursor-pointer" data-action="click->document-builder#moveBlock" data-direction="1" title="Move down">
            <i class="fa-solid fa-chevron-down text-xs"></i>
          </button>
          <button type="button" class="p-1 text-neutral-400 hover:text-red-500 cursor-pointer ml-1" data-action="click->document-builder#removeBlock" title="Remove block">
            <i class="fa-solid fa-trash-can text-xs"></i>
          </button>
        </div>
      </div>
    `
  }

  blockSummary(block) {
    const notConfigured = '<span class="text-neutral-400 italic">Not configured</span>'

    switch (block.type) {
      case "heading": {
        const text = (block.content || "").trim()
        return text ? this.escapeHtml(text) : notConfigured
      }
      case "text": {
        const tmp = document.createElement("div")
        tmp.innerHTML = block.content || ""
        const text = tmp.textContent.trim()
        if (!text) return notConfigured
        return this.escapeHtml(text.slice(0, 80)) + (text.length > 80 ? "..." : "")
      }
      case "divider":
        return '<span class="text-neutral-300">———</span>'
      case "merge_field": {
        const field = block.settings?.field || "practice_name"
        return `<span class="text-primary-600">{{${field.replace(/_/g, " ")}}}</span>`
      }
      case "signature":
        return this.escapeHtml(block.settings?.label || "Client Signature")
      case "checkbox": {
        const label = (block.settings?.label || "").trim()
        return label ? this.escapeHtml(label) : notConfigured
      }
      default: {
        const fieldLabel = (block.settings?.label || "").trim()
        return fieldLabel ? this.escapeHtml(fieldLabel) : notConfigured
      }
    }
  }

  expandBlock(event) {
    // Don't expand if clicking a control button
    if (event.target.closest("button")) return
    const blockEl = event.currentTarget.closest("[data-block-id]")
    if (!blockEl) return
    this.activeBlockIdValue = blockEl.dataset.blockId
    this.render()
  }

  collapseBlock() {
    this.activeBlockIdValue = ""
    this.render()
  }

  renderBlockToolbar(block, index) {
    const typeInfo = BLOCK_TYPES.find(t => t.type === block.type)
    const label = typeInfo ? typeInfo.label : block.type

    const mergeTagBtn = (block.type === "text" || block.type === "heading") ? `
      <span class="w-px h-3 bg-neutral-200 mx-0.5"></span>
      ${this.renderMergeTagButton()}
    ` : ""

    const textFormatButtons = block.type === "text" ? `
      <div class="flex items-center gap-0.5 ml-3 border-l border-neutral-200 pl-3" data-text-toolbar>
        <button type="button" class="p-1 rounded hover:bg-neutral-100 text-neutral-500 cursor-pointer transition-colors" data-action="mousedown->document-builder#formatText" data-command="bold" title="Bold">
          <i class="fa-solid fa-bold text-xs"></i>
        </button>
        <button type="button" class="p-1 rounded hover:bg-neutral-100 text-neutral-500 cursor-pointer transition-colors" data-action="mousedown->document-builder#formatText" data-command="italic" title="Italic">
          <i class="fa-solid fa-italic text-xs"></i>
        </button>
        <button type="button" class="p-1 rounded hover:bg-neutral-100 text-neutral-500 cursor-pointer transition-colors" data-action="mousedown->document-builder#formatText" data-command="underline" title="Underline">
          <i class="fa-solid fa-underline text-xs"></i>
        </button>
        <span class="w-px h-3 bg-neutral-200 mx-0.5"></span>
        <button type="button" class="p-1 rounded hover:bg-neutral-100 text-neutral-500 cursor-pointer transition-colors" data-action="mousedown->document-builder#formatText" data-command="insertUnorderedList" title="Bullet list">
          <i class="fa-solid fa-list-ul text-xs"></i>
        </button>
        <button type="button" class="p-1 rounded hover:bg-neutral-100 text-neutral-500 cursor-pointer transition-colors" data-action="mousedown->document-builder#formatText" data-command="insertOrderedList" title="Numbered list">
          <i class="fa-solid fa-list-ol text-xs"></i>
        </button>
        <span class="w-px h-3 bg-neutral-200 mx-0.5"></span>
        <button type="button" class="p-1 rounded hover:bg-neutral-100 text-neutral-500 cursor-pointer transition-colors" data-action="mousedown->document-builder#formatText" data-command="outdent" title="Decrease indent (Shift+Tab)">
          <i class="fa-solid fa-outdent text-xs"></i>
        </button>
        <button type="button" class="p-1 rounded hover:bg-neutral-100 text-neutral-500 cursor-pointer transition-colors" data-action="mousedown->document-builder#formatText" data-command="indent" title="Increase indent (Tab)">
          <i class="fa-solid fa-indent text-xs"></i>
        </button>
        ${mergeTagBtn}
      </div>
    ` : (block.type === "heading" ? `
      <div class="flex items-center gap-0.5 ml-3 border-l border-neutral-200 pl-3">
        ${mergeTagBtn}
      </div>
    ` : "")

    return `
      <div class="flex flex-wrap items-center justify-between gap-y-2 mb-2">
        <div class="flex items-center flex-wrap gap-y-1">
          <span class="text-xs font-medium text-neutral-400 uppercase tracking-wider">${label}</span>
          ${textFormatButtons}
        </div>
        <div class="flex items-center gap-1 flex-wrap justify-end">
          <button type="button" class="p-1 text-neutral-400 hover:text-neutral-600 cursor-pointer" data-action="click->document-builder#moveBlock" data-direction="-1" title="Move up">
            <i class="fa-solid fa-chevron-up text-xs"></i>
          </button>
          <button type="button" class="p-1 text-neutral-400 hover:text-neutral-600 cursor-pointer" data-action="click->document-builder#moveBlock" data-direction="1" title="Move down">
            <i class="fa-solid fa-chevron-down text-xs"></i>
          </button>
          <button type="button" class="p-1 text-neutral-400 hover:text-red-500 cursor-pointer ml-1" data-action="click->document-builder#removeBlock" title="Remove block">
            <i class="fa-solid fa-trash-can text-xs"></i>
          </button>
          <button type="button" class="ml-1 sm:ml-2 px-2 sm:px-2.5 py-1 text-xs font-medium text-neutral-500 bg-neutral-100 rounded-lg hover:bg-neutral-200 cursor-pointer transition-colors" data-action="click->document-builder#collapseBlock" title="Collapse block">
            Collapse
          </button>
        </div>
      </div>
    `
  }

  renderBlockContent(block) {
    switch (block.type) {
      case "heading":
        return this.renderHeadingBlock(block)
      case "text":
        return this.renderTextBlock(block)
      case "divider":
        return `<hr class="border-neutral-300 my-2" />`
      case "merge_field":
        return this.renderMergeFieldBlock(block)
      case "short_answer":
      case "long_answer":
      case "date":
      case "phone":
      case "email":
        return this.renderFieldBlock(block)
      case "single_select":
      case "multi_select":
        return this.renderSelectBlock(block)
      case "checkbox":
        return this.renderCheckboxBlock(block)
      case "signature":
        return this.renderSignatureBlock(block)
      default:
        return `<p class="text-sm text-neutral-500 italic">Unknown block type: ${this.escapeHtml(block.type)}</p>`
    }
  }

  renderHeadingBlock(block) {
    const level = block.settings?.level || "h2"
    const sizes = { h1: "text-2xl", h2: "text-xl", h3: "text-lg" }
    const labels = { h1: "Heading 1", h2: "Heading 2", h3: "Heading 3" }
    const options = [
      { value: "h1", label: "H1" },
      { value: "h2", label: "H2" },
      { value: "h3", label: "H3" }
    ]
    return `
      <div class="flex items-center gap-2 mb-2">
        ${this.renderCustomSelect(options, level, "level")}
      </div>
      <div contenteditable="true" class="${sizes[level] || "text-xl"} font-bold text-neutral-900 border border-neutral-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[2.5rem] empty:before:content-['Heading_text...'] empty:before:text-neutral-400 empty:before:font-normal" data-action="input->document-builder#updateBlockContent">${block.content || ""}</div>
    `
  }

  renderTextBlock(block) {
    return `
      <div contenteditable="true" class="text-sm text-neutral-700 border border-neutral-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[4rem] prose prose-sm max-w-none empty:before:content-['Write_your_content_here...'] empty:before:text-neutral-400" data-action="input->document-builder#updateBlockContent" data-controller="text-editor">${block.content || ""}</div>
    `
  }

  renderMergeFieldBlock(block) {
    const field = block.settings?.field || "client_name"
    const options = this.allMergeTags.map(f => ({
      value: f,
      label: f.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
    }))

    return `
      ${this.renderCustomSelect(options, field, "field")}
      <p class="text-xs text-neutral-400 mt-1">Will be replaced with the actual value when sent to a client.</p>
    `
  }

  renderFieldBlock(block) {
    const label = block.settings?.label || ""
    const required = block.settings?.required || false
    const helpText = block.settings?.help_text || ""
    const placeholder = block.settings?.placeholder || ""

    return `
      <div class="space-y-2">
        <div class="flex items-center gap-3">
          <input type="text" value="${this.escapeHtml(label)}" placeholder="Field label" class="flex-1 text-sm font-medium border border-neutral-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500" data-action="input->document-builder#updateBlockSetting" data-setting-key="label" />
          <label class="flex items-center gap-1.5 text-xs text-neutral-500 whitespace-nowrap cursor-pointer">
            <input type="checkbox" ${required ? "checked" : ""} class="w-4 h-4 accent-primary-600 rounded border-neutral-300" data-action="change->document-builder#updateBlockSetting" data-setting-key="required" />
            Required
          </label>
        </div>
        <input type="text" value="${this.escapeHtml(helpText)}" placeholder="Help text (optional)" class="w-full text-xs border border-neutral-200 rounded-lg px-3 py-1.5 text-neutral-500 focus:ring-2 focus:ring-primary-500/20" data-action="input->document-builder#updateBlockSetting" data-setting-key="help_text" />
        <input type="text" value="${this.escapeHtml(placeholder)}" placeholder="Placeholder text (optional)" class="w-full text-xs border border-neutral-200 rounded-lg px-3 py-1.5 text-neutral-500 focus:ring-2 focus:ring-primary-500/20" data-action="input->document-builder#updateBlockSetting" data-setting-key="placeholder" />
        <div class="bg-neutral-50 rounded-lg px-3 py-2 text-sm text-neutral-400 italic">
          ${this.fieldPreview(block.type, placeholder)}
        </div>
      </div>
    `
  }

  renderSelectBlock(block) {
    const label = block.settings?.label || ""
    const required = block.settings?.required || false
    const options = block.settings?.options || ""
    const helpText = block.settings?.help_text || ""

    return `
      <div class="space-y-2">
        <div class="flex items-center gap-3">
          <input type="text" value="${this.escapeHtml(label)}" placeholder="Field label" class="flex-1 text-sm font-medium border border-neutral-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500" data-action="input->document-builder#updateBlockSetting" data-setting-key="label" />
          <label class="flex items-center gap-1.5 text-xs text-neutral-500 whitespace-nowrap cursor-pointer">
            <input type="checkbox" ${required ? "checked" : ""} class="w-4 h-4 accent-primary-600 rounded border-neutral-300" data-action="change->document-builder#updateBlockSetting" data-setting-key="required" />
            Required
          </label>
        </div>
        <input type="text" value="${this.escapeHtml(options)}" placeholder="Options (comma-separated): Option A, Option B, Option C" class="w-full text-sm border border-neutral-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-primary-500/20" data-action="input->document-builder#updateBlockSetting" data-setting-key="options" />
        <input type="text" value="${this.escapeHtml(helpText)}" placeholder="Help text (optional)" class="w-full text-xs border border-neutral-200 rounded-lg px-3 py-1.5 text-neutral-500 focus:ring-2 focus:ring-primary-500/20" data-action="input->document-builder#updateBlockSetting" data-setting-key="help_text" />
      </div>
    `
  }

  renderCheckboxBlock(block) {
    const label = block.settings?.label || ""
    const required = block.settings?.required || false

    return `
      <div class="space-y-2">
        <div class="flex items-center gap-3">
          <input type="text" value="${this.escapeHtml(label)}" placeholder="Checkbox label (e.g. I agree to...)" class="flex-1 text-sm font-medium border border-neutral-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500" data-action="input->document-builder#updateBlockSetting" data-setting-key="label" />
          <label class="flex items-center gap-1.5 text-xs text-neutral-500 whitespace-nowrap cursor-pointer">
            <input type="checkbox" ${required ? "checked" : ""} class="w-4 h-4 accent-primary-600 rounded border-neutral-300" data-action="change->document-builder#updateBlockSetting" data-setting-key="required" />
            Required
          </label>
        </div>
        <div class="bg-neutral-50 rounded-lg px-3 py-2 flex items-center gap-2 text-sm text-neutral-400">
          <input type="checkbox" disabled class="rounded border-neutral-300" />
          <span>${this.escapeHtml(label) || "Checkbox preview"}</span>
        </div>
      </div>
    `
  }

  renderSignatureBlock(block) {
    return `
      <div class="bg-neutral-50 border-2 border-dashed border-neutral-300 rounded-lg p-6 text-center">
        <i class="fa-solid fa-signature text-2xl text-neutral-300 mb-2"></i>
        <p class="text-sm text-neutral-500">Signature capture area</p>
        <p class="text-xs text-neutral-400 mt-1">Client will draw or type their signature here</p>
      </div>
    `
  }

  renderInlineAddButton(index, isExpanded) {
    // Expanded: always visible on mobile, hover on desktop
    // Collapsed: hover only (desktop), hidden on mobile
    const visibility = isExpanded
      ? "opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
      : "opacity-0 group-hover:opacity-100 hidden sm:block"

    return `
      <div class="absolute left-1/2 ${visibility} transition-opacity z-10 inline-add-button-wrapper">
        <button type="button" data-add-block-btn data-after-index="${index}" data-action="click->document-builder#showMenu" class="w-7 h-7 rounded-full bg-primary-600 text-white hover:bg-primary-700 transition-colors cursor-pointer shadow-sm inline-add-button" title="Add block">
          <i class="fa-solid fa-plus inline-add-button-icon"></i>
        </button>
      </div>
    `
  }

  renderAddButton(afterIndex) {
    if (this.blocksValue.length === 0) {
      return `
        <div class="border-2 border-dashed border-neutral-300 rounded-lg p-8 text-center">
          <i class="fa-solid fa-plus-circle text-3xl text-neutral-300 mb-3"></i>
          <p class="text-sm text-neutral-500 mb-3">No blocks yet. Click below to start building.</p>
          <button type="button" data-add-block-btn data-after-index="-1" data-action="click->document-builder#showMenu" class="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer">
            <i class="fa-solid fa-plus text-xs"></i>
            Add First Block
          </button>
        </div>
      `
    }
    return ""
  }

  renderMenu() {
    const categories = [
      { key: "content", label: "Content" },
      { key: "field", label: "Form Fields" }
    ]

    const sections = categories.map(cat => {
      const types = BLOCK_TYPES.filter(t => t.category === cat.key)
      const items = types.map(t => `
        <button type="button" class="flex items-center gap-2 w-full px-3 py-2 text-sm text-neutral-700 hover:bg-primary-50 hover:text-primary-700 rounded-md transition-colors cursor-pointer" data-action="click->document-builder#addBlock" data-block-type="${t.type}" data-after-index="">
          <i class="fa-solid ${t.icon} w-4 text-center text-neutral-400"></i>
          <span>${t.label}</span>
        </button>
      `).join("")

      return `
        <div>
          <div class="px-3 py-1.5 text-xs font-semibold text-neutral-400 uppercase tracking-wider">${cat.label}</div>
          ${items}
        </div>
      `
    }).join('<hr class="my-1 border-neutral-200" />')

    return `
      <div data-document-builder-target="menu" class="hidden fixed z-50 w-[calc(100vw-2rem)] sm:w-56 bg-white border border-neutral-200 rounded-lg shadow-lg py-2 max-h-80 overflow-y-auto">
        ${sections}
      </div>
    `
  }

  // --- Merge Tag Insertion ---

  insertMergeTag(event) {
    event.preventDefault()
    event.stopPropagation()
    const tag = event.currentTarget.dataset.mergeTag
    if (!tag) return

    const blockEl = event.currentTarget.closest("[data-block-id]")
    if (!blockEl) return
    const blockId = blockEl.dataset.blockId
    const tagText = `{{${tag}}}`

    // Find the contenteditable element in this block
    const editable = blockEl.querySelector("[contenteditable]")
    if (editable) {
      editable.focus()
      // TODO(rte-migration): paired with formatText above — both use
      // document.execCommand which is deprecated but still functional
      // across browsers. Replacing requires a real RTE library.
      document.execCommand("insertText", false, tagText)
      // Sync content back
      const blocks = [...this.blocksValue]
      const block = blocks.find(b => b.id === blockId)
      if (block) {
        block.content = editable.innerHTML
        this.blocksValue = blocks
        this.syncHiddenInput()
      }
    }

    // Close the merge tag menu (also tears down the scroll/resize listeners)
    const menu = event.currentTarget.closest("[data-merge-menu]")
    if (menu) this.closeMergeTagMenu(menu)
  }

  toggleMergeTagMenu(event) {
    event.preventDefault()
    event.stopPropagation()
    const btn = event.currentTarget
    const menu = btn.nextElementSibling || btn.parentElement.querySelector("[data-merge-menu]")
    if (!menu) return

    const isOpen = !menu.classList.contains("hidden")
    // Close all merge menus first
    this.element.querySelectorAll("[data-merge-menu]").forEach(m => {
      this.closeMergeTagMenu(m)
    })
    if (!isOpen) {
      this.openMergeTagMenu(btn, menu)
    }
  }

  openMergeTagMenu(btn, menu) {
    this.anchorMenuToTrigger(btn, menu, {
      cleanupKey: "_mergeTagCleanup",
      computePosition: (rect) => {
        const left = Math.max(8, rect.right - 208) // 208 = w-52 (13rem)
        const menuHeight = 288 // max-h-72 = 18rem = 288px
        const top = (rect.bottom + 4 + menuHeight > window.innerHeight)
          ? rect.top - menuHeight - 4
          : rect.bottom + 4
        return { top, left }
      },
      shouldClose: (e) => !menu.contains(e.target) && !btn.contains(e.target),
      onClose: () => this.closeMergeTagMenu(menu)
    })
  }

  closeMergeTagMenu(menu) {
    menu.classList.add("hidden")
    menu.style.position = ""
    menu.style.top = ""
    menu.style.left = ""
    if (menu._mergeTagCleanup) {
      menu._mergeTagCleanup()
      menu._mergeTagCleanup = null
    }
  }

  // Generic helper: position `menu` relative to `trigger` via fixed positioning,
  // keep it anchored as the page scrolls (rAF tick), and close on click outside.
  // Used by both the merge-tag toolbar dropdown and the merge-field block select.
  anchorMenuToTrigger(trigger, menu, opts) {
    const { cleanupKey, computePosition, shouldClose, onClose, applyExtra } = opts
    let lastTop = null
    let lastLeft = null
    let lastExtra = null

    const positionMenu = () => {
      if (!trigger.isConnected) {
        onClose()
        return
      }
      const rect = trigger.getBoundingClientRect()
      const { top, left, extra } = computePosition(rect)
      const extraKey = extra ? JSON.stringify(extra) : null
      if (top === lastTop && left === lastLeft && extraKey === lastExtra) return
      lastTop = top
      lastLeft = left
      lastExtra = extraKey
      menu.style.position = "fixed"
      menu.style.top = `${top}px`
      menu.style.left = `${left}px`
      if (extra && applyExtra) applyExtra(menu, extra)
    }

    positionMenu()
    menu.classList.remove("hidden")

    // rAF loop keeps the menu anchored to the trigger regardless of which
    // ancestor scrolls. Capture-phase scroll listeners on window are
    // unreliable across nested overflow containers in this layout.
    let rafId = requestAnimationFrame(function tick() {
      positionMenu()
      rafId = requestAnimationFrame(tick)
    })

    // Register cleanup IMMEDIATELY so close paths in the same tick
    // (e.g., the close-all-menus loop in toggle*) can tear down rAF
    // before the click-outside handler is even attached.
    let clickHandler = null
    menu[cleanupKey] = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
        rafId = null
      }
      if (clickHandler) {
        document.removeEventListener("click", clickHandler)
        clickHandler = null
      }
    }

    // Delay click-outside attachment so the click that opened the menu
    // doesn't immediately close it.
    setTimeout(() => {
      // If the menu was already closed during the delay, skip.
      if (rafId === null) return
      clickHandler = (e) => {
        if (shouldClose(e)) onClose()
      }
      document.addEventListener("click", clickHandler)
    }, 10)
  }

  renderMergeTagButton() {
    const groups = this.mergeTagGroups
    const sections = Object.entries(groups).map(([group, tags]) => {
      const items = tags.map(t => {
        const label = t.replace(/^(client_|practice_|practitioner_)/, "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
        return `<button type="button" class="w-full text-left px-3 py-1.5 text-sm text-neutral-700 hover:bg-primary-50 hover:text-primary-700 rounded cursor-pointer" data-action="click->document-builder#insertMergeTag" data-merge-tag="${t}">${label}</button>`
      }).join("")
      return `
        <div class="px-3 py-1 text-xs font-semibold text-neutral-400 uppercase tracking-wider">${this.escapeHtml(group)}</div>
        ${items}
      `
    }).join('<hr class="my-1 border-neutral-100" />')

    return `
      <div class="relative inline-block">
        <button type="button" class="p-1 rounded hover:bg-neutral-100 text-neutral-500 cursor-pointer transition-colors" data-action="click->document-builder#toggleMergeTagMenu" title="Insert merge tag">
          <i class="fa-solid fa-code text-xs"></i>
        </button>
        <div data-merge-menu class="hidden z-50 w-52 bg-white border border-neutral-200 rounded-lg shadow-lg py-1 max-h-72 overflow-y-auto">
          ${sections}
        </div>
      </div>
    `
  }

  // --- Custom Select (matches AppFormBuilder select_input) ---

  renderCustomSelect(options, selectedValue, settingKey) {
    const selected = options.find(o => o.value === selectedValue) || options[0]
    const optionsHtml = options.map(o => `
      <button type="button"
              class="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-neutral-50 transition-colors cursor-pointer"
              data-action="click->document-builder#selectOption"
              data-select-value="${o.value}"
              data-setting-key="${settingKey}">
        <span>${this.escapeHtml(o.label)}</span>
        <i class="fa-solid fa-check text-xs text-primary-600 ${o.value === selectedValue ? "" : "invisible"}"></i>
      </button>
    `).join("")

    return `
      <div class="relative inline-block" data-custom-select>
        <button type="button"
                class="flex items-center justify-between gap-2 rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm text-neutral-900 cursor-pointer hover:border-neutral-400 transition-colors min-w-[7rem]"
                data-action="click->document-builder#toggleCustomSelect">
          <span data-select-label>${this.escapeHtml(selected.label)}</span>
          <i class="fa-solid fa-chevron-down text-xs text-neutral-400"></i>
        </button>
        <div class="hidden z-50 min-w-[10rem] bg-white border border-neutral-200 rounded-lg shadow-lg overflow-hidden" data-select-menu>
          ${optionsHtml}
        </div>
      </div>
    `
  }

  toggleCustomSelect(event) {
    event.stopPropagation()
    const btn = event.currentTarget
    const wrapper = btn.closest("[data-custom-select]")
    const menu = wrapper.querySelector("[data-select-menu]")
    const isOpen = !menu.classList.contains("hidden")

    // Close all other custom selects first
    this.element.querySelectorAll("[data-select-menu]").forEach(m => {
      this.closeCustomSelect(m)
    })

    if (!isOpen) {
      this.openCustomSelect(btn, wrapper, menu)
    }
  }

  openCustomSelect(btn, wrapper, menu) {
    this.anchorMenuToTrigger(btn, menu, {
      cleanupKey: "_customSelectCleanup",
      computePosition: (rect) => ({
        top: rect.bottom + 4,
        left: rect.left,
        extra: { minWidth: Math.max(rect.width, 160) }
      }),
      applyExtra: (m, extra) => { m.style.minWidth = `${extra.minWidth}px` },
      shouldClose: (e) => !wrapper.contains(e.target),
      onClose: () => this.closeCustomSelect(menu)
    })
  }

  closeCustomSelect(menu) {
    menu.classList.add("hidden")
    menu.style.position = ""
    menu.style.top = ""
    menu.style.left = ""
    menu.style.minWidth = ""
    if (menu._customSelectCleanup) {
      menu._customSelectCleanup()
      menu._customSelectCleanup = null
    }
  }

  selectOption(event) {
    event.stopPropagation()
    const value = event.currentTarget.dataset.selectValue
    const settingKey = event.currentTarget.dataset.settingKey
    const wrapper = event.currentTarget.closest("[data-custom-select]")
    const menu = wrapper.querySelector("[data-select-menu]")
    const label = wrapper.querySelector("[data-select-label]")

    // Update label
    label.textContent = event.currentTarget.querySelector("span").textContent

    // Update checkmarks
    menu.querySelectorAll(".fa-check").forEach(c => c.classList.add("invisible"))
    event.currentTarget.querySelector(".fa-check").classList.remove("invisible")

    // Close menu (also tears down rAF + click-outside listener)
    this.closeCustomSelect(menu)

    // Update block setting
    const blockEl = event.currentTarget.closest("[data-block-id]")
    if (blockEl) {
      const id = blockEl.dataset.blockId
      const blocks = [...this.blocksValue]
      const block = blocks.find(b => b.id === id)
      if (block) {
        if (!block.settings) block.settings = {}
        block.settings[settingKey] = value
        this.blocksValue = blocks
        this.syncHiddenInput()
        // Re-render for heading level changes (affects text size)
        if (settingKey === "level") this.render()
      }
    }
  }

  // --- Helpers ---

  fieldPreview(type, placeholder) {
    const p = this.escapeHtml(placeholder)
    switch (type) {
      case "short_answer": return `<span class="border-b border-neutral-300 inline-block w-48">${p || "Short answer"}</span>`
      case "long_answer": return `<span class="border border-neutral-300 inline-block w-full h-16 rounded">${p || "Long answer"}</span>`
      case "date": return `<span class="border border-neutral-300 inline-block px-2 py-1 rounded">MM/DD/YYYY</span>`
      case "phone": return `<span class="border border-neutral-300 inline-block px-2 py-1 rounded">(555) 123-4567</span>`
      case "email": return `<span class="border border-neutral-300 inline-block px-2 py-1 rounded">email@example.com</span>`
      default: return ""
    }
  }

  defaultSettings(type) {
    switch (type) {
      case "heading":
        return { level: "h2" }
      case "merge_field":
        return { field: "client_name" }
      case "short_answer":
      case "long_answer":
      case "date":
      case "phone":
      case "email":
        return { label: "", required: false, help_text: "", placeholder: "" }
      case "single_select":
      case "multi_select":
        return { label: "", required: false, options: "", help_text: "" }
      case "checkbox":
        return { label: "", required: false }
      case "signature":
        return { label: "Client Signature", required: true }
      default:
        return {}
    }
  }

  reindex(blocks) {
    blocks.forEach((b, i) => b.position = i)
  }

  syncHiddenInput() {
    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = JSON.stringify(this.blocksValue)
    }
  }

  blocksValueChanged() {
    this.syncHiddenInput()
  }

  escapeHtml(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
